import { useCallback, useEffect, useRef } from 'react';
import { pb } from '../lib/pb';

/* Records that a series was actually played.
 *
 * ── The rule this file is written around ──
 * IT MUST NEVER AFFECT THE GAME. Not by a frame, not by an error, not by a rerender. A
 * telemetry row is worth nothing next to a take, so every call here is fire-and-forget, every
 * failure is swallowed, and nothing it does can reach React state. If the whole thing breaks,
 * the recording is unaffected and the team loses a row.
 *
 * That rule is why this is a hook full of refs rather than state: a `useState` here would
 * rerender the play surface every time a beacon came back, mid-recording, for no reason.
 *
 * ── Why it updates as it goes, rather than once at the end ──
 * A recording ends by closing the tab, not by clicking anything. Waiting for the wrap screen
 * would lose every abandoned session — and those are the informative ones, because "he stopped
 * at 4" is worth knowing. `pagehide` was the obvious alternative and is not reliable on its own:
 * sendBeacon cannot carry the auth header the roster-gated route needs. So progress is written
 * as it changes and deduped on the payload, which costs about twenty requests across a ten
 * minute session and survives the tab being closed without warning.
 */

export type SessionSource = 'share' | 'modes' | 'preview';

interface Progress {
  reached: number;
  revealed: number;
  realCount: number;
  fakeCount: number;
  total: number;
  finished: boolean;
}

/** Where the row is written. A share link authorises with its token; the rest need the roster. */
function endpoint(source: SessionSource, key: string, sessionId?: string): string {
  const base = source === 'share'
    ? `/api/rof/play/${encodeURIComponent(key)}/session`
    : `/api/rof/rounds/${encodeURIComponent(key)}/session`;
  return sessionId ? `${base}/${encodeURIComponent(sessionId)}` : base;
}

export function useSessionRecord(
  source: SessionSource | null,
  /** The share token for `share`, otherwise the round id. */
  key: string,
  total: number,
) {
  const sessionId = useRef<string | null>(null);
  const starting = useRef(false);
  /* The last payload actually sent. Reveals can fire faster than the network answers, and
     re-sending an identical row is pure noise. */
  const lastSent = useRef('');

  const send = useCallback(
    async (path: string, body: Record<string, unknown>) => {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      // The share route needs no token; the others are behind the roster gate.
      if (source !== 'share' && pb.authStore.token) headers.Authorization = `Bearer ${pb.authStore.token}`;
      const res = await fetch(path, { method: 'POST', headers, body: JSON.stringify(body) });
      return (await res.json().catch(() => null)) as { ok?: boolean; id?: string } | null;
    },
    [source],
  );

  /** Called once, when START is pressed. */
  const begin = useCallback(() => {
    if (!source || !key || sessionId.current || starting.current) return;
    starting.current = true;
    void send(endpoint(source, key), { source, total })
      .then((r) => { if (r?.id) sessionId.current = r.id; })
      .catch(() => { /* a lost row must never surface to the person recording */ })
      .finally(() => { starting.current = false; });
  }, [key, send, source, total]);

  /** Called whenever progress changes. Identical payloads are dropped rather than re-sent. */
  const update = useCallback(
    (p: Progress) => {
      if (!source || !key || !sessionId.current) return;
      const payload = { ...p, source };
      const stamp = JSON.stringify(payload);
      if (stamp === lastSent.current) return;
      lastSent.current = stamp;
      void send(endpoint(source, key, sessionId.current), payload).catch(() => {});
    },
    [key, send, source],
  );

  /* A last write when the tab goes away, so a session abandoned mid-run still records where it
     got to. Best effort by definition — the browser may kill the request — which is exactly why
     the reveal-by-reveal updates above are the real mechanism and this is only a top-up. */
  const latest = useRef<Progress | null>(null);
  const remember = useCallback((p: Progress) => { latest.current = p; }, []);
  useEffect(() => {
    const flush = () => { if (latest.current) update(latest.current); };
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, [update]);

  return { begin, update, remember };
}
